import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Users, Briefcase } from "lucide-react";

const ADS_LABEL = {
  good: { label: "Good", color: "#10B981" },
  average: { label: "Average", color: "#F59E0B" },
  bad: { label: "Bad", color: "#EF4444" },
  on_hold: { label: "On Hold", color: "#737373" },
};

export default function Reports() {
  const [tab, setTab] = useState("team");
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [team, setTeam] = useState(null);
  const [clientRpt, setClientRpt] = useState(null);

  const loadTeam = async () => {
    try {
      const r = await api.get("/reports/team", { params: { days } });
      setTeam(r.data);
    } catch (_) {}
  };
  const loadClient = async () => {
    try {
      const r = await api.get("/reports/client", { params: { month } });
      setClientRpt(r.data);
    } catch (_) {}
  };

  useEffect(() => { if (tab === "team") loadTeam(); else loadClient(); }, [tab, days, month]);

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="Insights"
        title="Reports"
        description="Team productivity and per-client performance. Admin-only."
      />

      <div className="px-6 lg:px-10 py-8 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex border border-neutral-800">
            <button
              onClick={() => setTab("team")}
              data-testid="tab-team"
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2 ${
                tab === "team" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-white"
              }`}
            >
              <Users className="w-3 h-3" /> Team Report
            </button>
            <button
              onClick={() => setTab("client")}
              data-testid="tab-client"
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2 border-l border-neutral-800 ${
                tab === "client" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-white"
              }`}
            >
              <Briefcase className="w-3 h-3" /> Client Report
            </button>
          </div>
          {tab === "team" ? (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Window</span>
              <Input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))}
                data-testid="report-days-input"
                className="w-24 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
              />
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">days</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Month</span>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="report-month-input"
                className="w-44 h-8 rounded-none bg-neutral-900 border-neutral-800 text-xs"
              />
            </div>
          )}
        </div>

        {/* Team Report */}
        {tab === "team" && team && (
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <div className="col-span-3">Member</div>
              <div className="col-span-1 text-right">Days</div>
              <div className="col-span-2 text-right">Avg / day · Δ vs 8h</div>
              <div className="col-span-2 text-right">Videos Edited</div>
              <div className="col-span-2 text-right">Revisions</div>
              <div className="col-span-2 text-right">Delays</div>
            </div>
            {team.rows.map((r) => (
              <div
                key={r.user_id}
                className="px-5 py-4 grid grid-cols-12 gap-3 border-b border-neutral-900 text-sm"
                data-testid={`team-report-row-${r.user_id}`}
              >
                <div className="col-span-3">
                  <div className="font-medium">{r.user_name}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{r.role}</div>
                </div>
                <div className="col-span-1 text-right font-mono">{r.days}</div>
                <div className="col-span-2 text-right font-mono">
                  {r.avg_hours} h
                  <span className={`ml-2 text-xs ${r.delta_hours >= 0 ? "text-emerald-400" : "text-amber-400"}`}>
                    {r.delta_hours >= 0 ? "+" : ""}{r.delta_hours}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono text-[#00C97A]">{r.videos_edited}</div>
                <div className={`col-span-2 text-right font-mono ${r.revisions > 0 ? "text-amber-400" : "text-neutral-500"}`}>{r.revisions}</div>
                <div className={`col-span-2 text-right font-mono ${r.delays > 0 ? "text-red-400" : "text-neutral-500"}`}>{r.delays}</div>
              </div>
            ))}
            <div className="px-5 py-4 grid grid-cols-12 gap-3 bg-neutral-900 text-sm">
              <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] text-neutral-400">Team total</div>
              <div className="col-span-1 text-right font-mono">{team.totals.days}</div>
              <div className="col-span-2 text-right font-mono">{team.totals.total_hours} h</div>
              <div className="col-span-2 text-right font-mono text-[#00C97A]">{team.totals.videos_edited}</div>
              <div className="col-span-2 text-right font-mono">{team.totals.revisions}</div>
              <div className="col-span-2 text-right font-mono">{team.totals.delays}</div>
            </div>
          </div>
        )}

        {/* Client Report */}
        {tab === "client" && clientRpt && (
          <div className="border border-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-900 grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <div className="col-span-3">Client</div>
              <div className="col-span-2">Ads</div>
              <div className="col-span-1 text-right">Videos</div>
              <div className="col-span-1 text-right">Graphics</div>
              <div className="col-span-1 text-right">Shoots</div>
              <div className="col-span-2 text-right">Shoot ₹</div>
              <div className="col-span-2 text-right">Due ₹</div>
            </div>
            {clientRpt.rows.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-neutral-500">No clients yet.</div>
            )}
            {clientRpt.rows.map((r) => {
              const ads = r.ads_status ? ADS_LABEL[r.ads_status] : null;
              return (
                <div
                  key={r.client_id}
                  className="px-5 py-4 grid grid-cols-12 gap-3 border-b border-neutral-900 text-sm"
                  data-testid={`client-report-row-${r.client_id}`}
                >
                  <div className="col-span-3">
                    <div className="font-medium">{r.name}</div>
                    {r.on_hold && <span className="text-[9px] uppercase tracking-[0.15em] text-red-400">ON HOLD</span>}
                  </div>
                  <div className="col-span-2">
                    {ads ? (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border"
                        style={{ borderColor: ads.color, color: ads.color }}
                      >
                        {ads.label}
                      </span>
                    ) : (
                      <span className="text-[10px] text-neutral-600">— not set —</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-[#00C97A]">{r.videos_posted}</div>
                  <div className="col-span-1 text-right font-mono text-[#8B5CF6]">{r.graphics_published}</div>
                  <div className="col-span-1 text-right font-mono">{r.shoots_done}/{r.monthly_shoot_target || "—"}</div>
                  <div className="col-span-2 text-right font-mono">₹ {Number(r.shoot_revenue).toLocaleString()}</div>
                  <div className={`col-span-2 text-right font-mono ${r.amount_due > 0 ? "text-amber-400" : "text-neutral-500"}`}>₹ {Number(r.amount_due).toLocaleString()}</div>
                </div>
              );
            })}
            <div className="px-5 py-4 grid grid-cols-12 gap-3 bg-neutral-900 text-sm">
              <div className="col-span-5 text-[10px] uppercase tracking-[0.2em] text-neutral-400">Totals — {clientRpt.month}</div>
              <div className="col-span-1 text-right font-mono text-[#00C97A]">{clientRpt.totals.videos_posted}</div>
              <div className="col-span-1 text-right font-mono text-[#8B5CF6]">{clientRpt.totals.graphics_published}</div>
              <div className="col-span-1 text-right font-mono">{clientRpt.totals.shoots_done}</div>
              <div className="col-span-2 text-right font-mono">₹ {Number(clientRpt.totals.shoot_revenue).toLocaleString()}</div>
              <div className="col-span-2 text-right font-mono">₹ {Number(clientRpt.totals.amount_due).toLocaleString()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
